import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getAuthToken } from "../../../utils/getAuthToken";

const SERVER_DOMAIN = process.env.REACT_APP_SERVER_DOMAIN || 'http://localhost:8000';

const notifDebounceMap = new Map();
const NOTIF_DEBOUNCE_TIME = 5000;
const shouldDebounceNotif = () => {
  const now = Date.now();
  const last = notifDebounceMap.get('fetchNotificationSettings');
  if (last && (now - last) < NOTIF_DEBOUNCE_TIME) return true;
  notifDebounceMap.set('fetchNotificationSettings', now);
  return false;
};

// Clears the module-level debounce map — call on logout so the next
// account's first fetch is never silently skipped as "too soon".
export const __resetNotificationCaches = () => {
  notifDebounceMap.clear();
};

// Fetch notification settings
export const fetchNotificationSettings = createAsyncThunk(
  "notifications/fetchSettings",
  async (_, { rejectWithValue }) => {
    if (shouldDebounceNotif()) return rejectWithValue("Debounced");

    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const response = await fetch(
        `${SERVER_DOMAIN}/api/notifications`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch notification settings");
      }

      const data = await response.json();
      return data.settings;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Update notification settings
export const updateNotificationSettings = createAsyncThunk(
  "notifications/updateSettings",
  async (settings, { rejectWithValue }) => {
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const response = await fetch(
        `${SERVER_DOMAIN}/api/notifications`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(settings),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update notification settings");
      }

      const data = await response.json();
      return data.settings;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Account-wide notification sound (Settings -> Notifications). Upload/reset are
// permission-gated server-side (Notifications.Edit, Owner bypasses) -- these
// thunks don't need to know that, a 403 just surfaces as a normal rejection.
export const uploadNotificationSound = createAsyncThunk(
  "notifications/uploadSound",
  async (file, { rejectWithValue }) => {
    const token = await getAuthToken();
    if (!token) return rejectWithValue("No token found");

    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await fetch(`${SERVER_DOMAIN}/api/notifications/sound`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || "Failed to upload notification sound");
      }
      return { soundUrl: data.soundUrl, soundFileName: data.soundFileName };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const resetNotificationSound = createAsyncThunk(
  "notifications/resetSound",
  async (_, { rejectWithValue }) => {
    const token = await getAuthToken();
    if (!token) return rejectWithValue("No token found");

    try {
      const response = await fetch(`${SERVER_DOMAIN}/api/notifications/sound`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || "Failed to reset notification sound");
      }
      return null;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  settings: {
    notifyOnAllIncomingMessages: true,
    notifyOnMessagesFromAssignedContacts: false,
    notifyOnAssignment: true,
    soundEnabled: true,
    browserNotificationsEnabled: true,
    channels: [],
    // Account-wide, shared by every team member. null = app's built-in default.
    soundUrl: null,
    // Original filename it was uploaded as (e.g. "ding.mp3") -- purely for
    // display, so the Settings UI can show what's actually been uploaded.
    soundFileName: null
  },
  loading: false,
  error: null,
};

const notificationSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    // Live push from the server (notification_sound_updated socket event) when
    // any team member on this account changes the shared sound -- see
    // socketInboxUpdater.js's listener. Keeps every open tab in sync without
    // waiting for a reload/refetch.
    soundUrlPushed: (state, action) => {
      state.settings.soundUrl = action.payload?.soundUrl ?? null;
      state.settings.soundFileName = action.payload?.soundFileName ?? null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotificationSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNotificationSettings.fulfilled, (state, action) => {
        state.loading = false;
        state.settings = action.payload;
        state.error = null;
      })
      .addCase(fetchNotificationSettings.rejected, (state, action) => {
        state.loading = false;
        if (action.payload !== "Debounced") {
          state.error = action.payload;
        }
      })
      .addCase(updateNotificationSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateNotificationSettings.fulfilled, (state, action) => {
        state.loading = false;
        state.settings = action.payload;
        state.error = null;
      })
      .addCase(updateNotificationSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(uploadNotificationSound.fulfilled, (state, action) => {
        state.settings.soundUrl = action.payload.soundUrl;
        state.settings.soundFileName = action.payload.soundFileName;
      })
      .addCase(resetNotificationSound.fulfilled, (state) => {
        state.settings.soundUrl = null;
        state.settings.soundFileName = null;
      });
  },
});

export const { soundUrlPushed } = notificationSlice.actions;
export default notificationSlice.reducer;

